import React, { useEffect, useState } from "react";
import { Alert, Button, DatePicker, Modal, Segmented, Space, Typography } from "antd";
import { CopyOutlined, FileAddOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { getClause, insertClause } from "./api";
import { conditionLabels } from "./labels";

/**
 * The funding condition for the contract (aufschiebende / auflösende Bedingung): preview, copy,
 * and append it once to the linked draft offer's text.
 */
const ClauseCard = ({ view, onUpdated }) => {
  const { t } = useTranslation("dashboard");
  const data = view.data;
  const [condition, setCondition] = useState(data.contract?.condition || "suspensive");
  const [latestDate, setLatestDate] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const labels = conditionLabels(t);

  useEffect(() => {
    getClause(data._id, {
      condition,
      latestDate: latestDate ? latestDate.format("YYYY-MM-DD") : undefined
    })
      .then(res => setText(res.data.text))
      .catch(generalErrorAlert);
  }, [data._id, condition, latestDate, data.contract?.plannedDate]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      Success(t("Copied"));
    } catch {
      generalErrorAlert({ message: t("Copying is not available in this browser.") });
    }
  };

  const insert = () =>
    Modal.confirm({
      rootClassName: "funding-page",
      title: t("Add the clause to the offer?"),
      content: t(
        "The text is a draft. Have it checked by your lawyer before you use it. It is appended once to the offer text; the positions stay unchanged."
      ),
      okText: t("Add to offer"),
      cancelText: t("Cancel"),
      onOk: async () => {
        setBusy(true);
        try {
          const res = await insertClause(data._id, {
            condition,
            latestDate: latestDate ? latestDate.format("YYYY-MM-DD") : undefined
          });
          Success(
            res.data.inserted
              ? t("The clause was added to the offer.")
              : t("The offer already contains the clause.")
          );
          onUpdated(res.data);
        } catch (error) {
          generalErrorAlert(error);
        } finally {
          setBusy(false);
        }
      }
    });

  const offer = data.offer;
  return (
    <div>
      <Alert
        type="warning"
        showIcon
        className="mb-3"
        message={t(
          "BEG funding: sign the contract only with a funding condition, and only start the work after the application."
        )}
      />
      <Space wrap className="mb-3">
        <Segmented
          value={condition}
          onChange={setCondition}
          options={Object.entries(labels).map(([value, label]) => ({ value, label }))}
        />
        {condition === "suspensive" && (
          <DatePicker
            value={latestDate}
            onChange={setLatestDate}
            format="DD.MM.YYYY"
            placeholder={t("Approval expected by (optional)")}
          />
        )}
      </Space>
      <Typography.Paragraph className="fu-clause">{text}</Typography.Paragraph>
      {!data.contract?.plannedDate && view.program.conditionNeedsPlannedDate && (
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message={t(
            "Enter the planned implementation month in the overview so the clause states it."
          )}
        />
      )}
      <Space wrap>
        <Button icon={<CopyOutlined />} onClick={copy}>
          {t("Copy text")}
        </Button>
        {view.can.editOffer && view.can.update && (
          <Button
            type="primary"
            icon={<FileAddOutlined />}
            disabled={!offer}
            loading={busy}
            onClick={insert}
          >
            {offer
              ? t("Add to offer {{offer}}", { offer: offer.number || offer.title })
              : t("Link an offer first")}
          </Button>
        )}
      </Space>
      {data.contract?.clauseInsertedAt && (
        <Typography.Paragraph type="secondary" className="fu-small mt-2">
          {t("Clause added to the offer on {{date}}.", {
            date: new Date(data.contract.clauseInsertedAt).toLocaleDateString("de-DE")
          })}
        </Typography.Paragraph>
      )}
    </div>
  );
};

export default ClauseCard;
